import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, FAQSection, CTASection, FeatureList } from '../../components/PageTemplate';
import { 
  Home, Camera, Key, ClipboardCheck, Truck, 
  Banknote, CheckCircle, ArrowRight 
} from 'lucide-react';

export default function TenantsMoving() {
  const moveInSteps = [
    {
      title: 'Pre-Move Checklist',
      description: 'We send you a checklist: what to bring, utility setup reminders, and key collection details.'
    },
    {
      title: 'Check-In Inventory',
      description: 'Document the property condition via the app with timestamped photos. This protects you at move-out.'
    },
    {
      title: 'Key Collection',
      description: 'Collect keys at the agreed time. We\'ll confirm you\'ve moved in.'
    },
    {
      title: 'Utility Setup',
      description: 'Take meter readings and set up your utilities. We provide supplier details.'
    }
  ];

  const moveOutSteps = [
    {
      title: 'Give Notice',
      description: 'Submit your notice through the app. We confirm the date and next steps.'
    },
    {
      title: 'Pre-Checkout Guidance',
      description: 'We send a checklist: cleaning standards, repairs to address, items to return.'
    },
    {
      title: 'Check-Out Inspection',
      description: 'Property is inspected and compared against your check-in inventory.'
    },
    {
      title: 'Deposit Return',
      description: 'If all is well, your deposit is returned. Any deductions are itemized and evidenced.'
    }
  ];

  const features = [
    {
      icon: Camera,
      title: 'Photo Documentation',
      description: 'Timestamped photos at check-in protect you. Any existing damage is recorded.'
    },
    {
      icon: ClipboardCheck,
      title: 'Detailed Inventory',
      description: 'Room-by-room condition report you can review and dispute if needed.'
    },
    {
      icon: Banknote,
      title: 'Fair Deposit Process',
      description: 'Clear comparison between check-in and check-out. Deductions must be evidenced.'
    },
    {
      icon: CheckCircle,
      title: 'Dispute Resolution',
      description: 'Disagree with a deduction? Raise a dispute and we\'ll mediate fairly.'
    }
  ];

  const faqs = [
    {
      question: 'What should I check at move-in?',
      answer: 'Check and photograph: walls, floors, ceilings, windows, appliances, furniture (if furnished), bathroom fittings, and any existing damage. The more thorough you are, the more protected you are at move-out.'
    },
    {
      question: 'How much notice do I need to give?',
      answer: 'This depends on your tenancy type and jurisdiction. We\'ll tell you the exact notice period in your portal. It\'s usually 1-2 months.'
    },
    {
      question: 'How is my deposit returned?',
      answer: 'After check-out, we compare the property against your check-in inventory. If there\'s no damage beyond fair wear and tear, your full deposit is returned within 10 days.'
    },
    {
      question: 'What counts as "fair wear and tear"?',
      answer: 'Normal use over time: minor scuffs on walls, carpet wear in high-traffic areas, faded curtains. Not fair wear and tear: holes in walls, stains, burns, broken items.'
    },
    {
      question: 'What if I disagree with deductions?',
      answer: 'You can raise a dispute. If we can\'t agree, the deposit scheme will adjudicate based on the evidence (your check-in photos are crucial here).'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="MOVING IN & OUT"
        title="Smooth Transitions"
        subtitle="Start to Finish"
        description="Clear processes for moving in and out. Photo documentation protects you. Fair deposit handling with no surprises."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
        secondaryCTA={{ href: '/tenants', label: 'Back to Overview' }}
        gradient="from-cyan-50 via-white to-slate-50"
      />

      <BenefitsGrid title="Protection at Every Step" benefits={features} />

      {/* Move In Process */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Key className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold">Moving In</h2>
              </div>
              <div className="space-y-4">
                {moveInSteps.map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold">{step.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold">Moving Out</h2>
              </div>
              <div className="space-y-4">
                {moveOutSteps.map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold">{step.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deposit Return Example */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Transparent Deposit Returns
          </h2>
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="font-semibold">Original Deposit</span>
              <span className="font-bold text-lg">£1,200.00</span>
            </div>
            <div className="py-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-red-600">Cleaning (below standard)</span>
                <span className="text-red-600">-£85.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-red-600">Wall repair (not fair wear)</span>
                <span className="text-red-600">-£45.00</span>
              </div>
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="font-semibold">Returned to You</span>
              <span className="font-bold text-lg text-green-600">£1,070.00</span>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              All deductions include photos and evidence. You can dispute any item.
            </p>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to find your next home?"
        description="Create your profile and start your search today."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
      />
    </PublicLayout>
  );
}
